// Stripe hands us raw requirement codes — `external_account`,
// `company.tax_id`, `individual.verification.document`. Neither a Thisway admin
// nor a restaurant owner should ever read one. This module owns the
// translation, and its whole reason to exist is the fallback: a code we have
// not mapped is humanised from the code itself and rendered anyway. An
// invisible blocker is the failure this module prevents, so nothing is ever
// dropped for being unrecognised.

const SSN_HINT = 'Stripe collects it directly; Thisway never receives it';
const ID_HINT = "Front and back of a driver's license or passport";
const COMPANY_ADDRESS = 'Business address';
const COMPANY_ADDRESS_HINT = 'The address the IRS has on file';
const TOS = "Acceptance of Stripe's terms";
const TOS_HINT = 'Signed inside the onboarding form';

// Keyed by Stripe's code. `label` is what the screen shows; `hint` is the quiet
// second line beside it. Several codes deliberately share a label — Stripe
// reports a missing date of birth as three codes and a missing address as four,
// and one row per fact reads far better than seven. groupRequirements()
// collapses them on the label for exactly that reason.
const REQUIREMENT_LABELS = {
  // Payouts
  external_account: {
    label: 'Business bank account',
    hint: 'Where payouts land — Stripe collects it, Thisway never sees it',
  },
  'documents.bank_account_ownership_verification.files': {
    label: 'Proof the bank account belongs to the business',
    hint: 'A recent statement or a voided check',
  },

  // Business profile
  business_type: {
    label: 'Business type',
    hint: 'Sole proprietor, LLC, corporation or non-profit',
  },
  'business_profile.name': { label: 'Public business name' },
  'business_profile.url': {
    label: 'Business website',
    hint: 'A public menu or a social page is accepted',
  },
  'business_profile.mcc': {
    label: 'Business category',
    hint: "Stripe's merchant category — restaurants are 5812",
  },
  'business_profile.product_description': {
    label: 'Description of what the business sells',
  },
  'business_profile.support_phone': {
    label: 'Customer support phone number',
  },
  'settings.card_payments.statement_descriptor_prefix': {
    label: 'Card statement name',
    hint: 'What diners see on their card statement',
  },
  // The sibling code, and the one a real account actually asks for: a US
  // account created on api-test came back requesting this on its very first
  // onboarding, where it was the only one of fifteen codes with no entry here.
  // Same label as the prefix above on purpose — a restaurant asked for both is
  // being asked one thing, and the label de-duplication collapses them.
  'settings.payments.statement_descriptor': {
    label: 'Card statement name',
    hint: 'What diners see on their card statement',
  },

  // Company (LLC, corporation, partnership)
  'company.name': {
    label: 'Legal business name',
    hint: 'Exactly as it appears on the EIN letter',
  },
  'company.tax_id': { label: 'Employer Identification Number (EIN)' },
  'company.phone': { label: 'Business phone number' },
  'company.address.line1': {
    label: COMPANY_ADDRESS,
    hint: COMPANY_ADDRESS_HINT,
  },
  'company.address.city': {
    label: COMPANY_ADDRESS,
    hint: COMPANY_ADDRESS_HINT,
  },
  'company.address.state': {
    label: COMPANY_ADDRESS,
    hint: COMPANY_ADDRESS_HINT,
  },
  'company.address.postal_code': {
    label: COMPANY_ADDRESS,
    hint: COMPANY_ADDRESS_HINT,
  },
  'company.verification.document': {
    label: 'Business verification document',
    hint: 'Usually the IRS EIN confirmation letter',
  },
  'company.owners_provided': {
    label: 'Confirmation that every owner is listed',
  },
  'company.directors_provided': {
    label: 'Confirmation that every director is listed',
  },
  'company.executives_provided': {
    label: 'Confirmation that every executive is listed',
  },
  'company.ownership_declaration.date': {
    label: 'Signed ownership declaration',
  },

  // The representative Stripe requires on a company account
  'representative.first_name': { label: "Representative's legal name" },
  'representative.last_name': { label: "Representative's legal name" },
  'representative.dob.day': { label: "Representative's date of birth" },
  'representative.dob.month': { label: "Representative's date of birth" },
  'representative.dob.year': { label: "Representative's date of birth" },
  'representative.email': { label: "Representative's email address" },
  'representative.phone': { label: "Representative's phone number" },
  'representative.address.line1': { label: "Representative's home address" },
  'representative.address.city': { label: "Representative's home address" },
  'representative.address.state': { label: "Representative's home address" },
  'representative.address.postal_code': {
    label: "Representative's home address",
  },
  'representative.relationship.title': { label: "Representative's job title" },
  'representative.id_number': {
    label: "Representative's Social Security number",
    hint: SSN_HINT,
  },
  'representative.ssn_last_4': {
    label: "Last four digits of the representative's SSN",
    hint: SSN_HINT,
  },
  'representative.verification.document': {
    label: "Representative's ID",
    hint: ID_HINT,
  },
  'representative.verification.additional_document': {
    label: 'A second document for the representative',
    hint: 'Usually proof of address',
  },

  // Sole proprietor / individual accounts
  'individual.first_name': { label: "Owner's legal name" },
  'individual.last_name': { label: "Owner's legal name" },
  'individual.dob.day': { label: "Owner's date of birth" },
  'individual.dob.month': { label: "Owner's date of birth" },
  'individual.dob.year': { label: "Owner's date of birth" },
  'individual.email': { label: "Owner's email address" },
  'individual.phone': { label: "Owner's phone number" },
  'individual.address.line1': { label: "Owner's home address" },
  'individual.address.city': { label: "Owner's home address" },
  'individual.address.state': { label: "Owner's home address" },
  'individual.address.postal_code': { label: "Owner's home address" },
  'individual.id_number': {
    label: "Owner's Social Security number",
    hint: SSN_HINT,
  },
  'individual.ssn_last_4': {
    label: "Last four digits of the owner's SSN",
    hint: SSN_HINT,
  },
  'individual.verification.document': { label: "Owner's ID", hint: ID_HINT },
  'individual.verification.additional_document': {
    label: 'A second document for the owner',
    hint: 'Usually proof of address',
  },

  // Terms
  'tos_acceptance.date': { label: TOS, hint: TOS_HINT },
  'tos_acceptance.ip': { label: TOS, hint: TOS_HINT },
};

// `person_1Mq7x2Sd…` is Stripe's id for an additional owner or director.
// Left raw it turns the fallback into gibberish. It cannot simply collapse to
// the word "person" either: Stripe emits one code per person per field, so the
// two owners of one LLC both arrive as `person_….id_number`, both humanise to
// the same words, and the label de-duplication below would silently merge two
// people into one row — an admin would read "one person outstanding" and be
// wrong. Each id therefore gets a stable ordinal within one grouping call.
const PERSON_ID = /^person_[A-Za-z0-9]+$/;

const personLabeller = () => {
  const ordinals = new Map();
  return (id) => {
    if (!ordinals.has(id)) ordinals.set(id, ordinals.size + 1);
    return 'Person ' + ordinals.get(id);
  };
};

// A bare describeRequirement() call sees one code and cannot know how many
// people there are, so it says "Person" with no number rather than claiming a
// number it cannot know. Grouped rendering — which is how both screens draw
// this list — numbers them.
const anonymousPerson = () => 'Person';

const humanizeCode = (code, labelPerson) => {
  const raw = typeof code === 'string' ? code.trim() : '';
  if (!raw) return 'Additional information';
  const words = raw
    .split('.')
    .map((part) => (PERSON_ID.test(part) ? labelPerson(part) : part))
    .join(' ')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!words) return 'Additional information';
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// Own-property lookup only. A plain object literal answers `constructor`,
// `toString` and `valueOf` from its prototype, so `REQUIREMENT_LABELS[code]` is
// truthy for them and `.label` is `undefined` — a requirement that exists in
// the data and renders as nothing. That invisible blocker is the one failure
// this module exists to prevent, so the lookup refuses inherited keys.
const entryFor = (code) =>
  Object.prototype.hasOwnProperty.call(REQUIREMENT_LABELS, code)
    ? REQUIREMENT_LABELS[code]
    : null;

/**
 * A readable label for one Stripe requirement code. Unmapped codes are
 * humanised from the code itself — never hidden. `labelPerson` is private to
 * this module: groupRequirements passes a numbering one so that two different
 * people never collapse into a single row, and a caller that passes only a code
 * gets the unnumbered wording.
 */
export const describeRequirement = (code, labelPerson = anonymousPerson) => {
  const mapped = entryFor(code);
  if (mapped?.label) return mapped.label;
  return humanizeCode(code, labelPerson);
};

const hintFor = (code) => entryFor(code)?.hint || '';

// Most urgent first. Stripe nests these sets — past_due is a subset of
// currently_due, which is a subset of eventually_due — so a code that has
// already been shown in a more urgent group must not be repeated below it.
const GROUP_ORDER = [
  { key: 'pastDue', title: 'Overdue' },
  { key: 'currentlyDue', title: 'Required now' },
  { key: 'eventuallyDue', title: 'Required later' },
];

/**
 * Turns a ConnectStatus `requirements` object into the ordered groups the UI
 * renders: overdue, then required now, then required later. Empty groups are
 * omitted. Codes are de-duplicated by their rendered label, so the three codes
 * Stripe emits for one missing date of birth become one row, and a code
 * already listed as overdue is not repeated under "required now". Stripe's
 * per-person codes are numbered within the call, so two owners of one business
 * stay two rows.
 */
export const groupRequirements = (requirements) => {
  const labelPerson = personLabeller();
  const seen = new Set();
  const groups = [];

  GROUP_ORDER.forEach(({ key, title }) => {
    const codes = Array.isArray(requirements?.[key]) ? requirements[key] : [];
    const items = [];

    codes.forEach((code) => {
      if (typeof code !== 'string') return;
      const trimmed = code.trim();
      if (!trimmed) return;
      const label = describeRequirement(trimmed, labelPerson);
      if (seen.has(label)) return;
      seen.add(label);
      items.push({ code: trimmed, label, hint: hintFor(trimmed) });
    });

    if (items.length) groups.push({ key, title, items });
  });

  return groups;
};
